/**
 * Shared types + helpers for the Customer Management console
 * (/customer-management). Data comes from /api/e2/console, which serves the
 * cached E2 snapshot (backend/e2_summit.py) plus the weekly-email settings
 * (backend/e2_console.py).
 */
import { API_BASE_URL } from '../config';

export interface E2Device {
  id: string;
  org: string;
  total_hours: number;
  recordings: number;
  week_hours: number;
  pace: number;
  days: number[];
  day_labels: string[];
  prev_week_hours: number;
  last30_hours: number;
  best_day: number;
  summit_days: number;
  first_recording: string | null;
  last_recording: string | null;
  active_days: number;
  history?: number[];
  never_recorded?: boolean;
  silent_days?: number | null;
}

export interface E2Org {
  org: string;
  headsets: string[];
  days: number[];
  history?: number[];
  total_hours: number;
  week_hours: number;
  last30_hours: number;
  prev_week_hours: number;
  last_recording: string | null;
  headset_count: number;
  active_week: number;
  at_pace: number;
  week_target_hours: number;
  pace: number;
  top: { id: string; week_hours: number; pace: number } | null;
  silent_days: number | null;
  /** Non-E2 devices on the org's roster; null when the roster couldn't be read. */
  body_cameras?: number | null;
}

export interface E2Snapshot {
  generatedAt: string;
  today: string;
  target: { pace_hours_per_day: number; week_hours_per_headset: number; summit_hours: number };
  headsets: {
    customer: number; orgs: number; active_week: number; at_pace_week: number;
    next_flag?: number; to_next_flag?: number;
  };
  hours: {
    total: number;
    next_camp?: number;
    to_next_camp?: number;
    at_pace_week_hours?: number;
    week: number;
    prev_week: number;
    fleet_pace: number;
    days: number[];
    day_labels: string[];
  };
  orgs: E2Org[];
  devices: E2Device[];
  history_labels?: string[];
}

export type EmailMode = 'inherit' | 'on' | 'off';

export interface LastSent {
  week: string;
  status: 'sending' | 'sent' | 'failed';
  at: string;
  to?: string[];
  subject?: string;
  attempts?: number;
}

export interface OrgEmail {
  mode: EmailMode;
  recipients: string[];
  enabled: boolean;
  /** Customers who also run body cameras never get a customer email. */
  body_cameras?: number | null;
  will_send: boolean;
  reason: string | null;
  last_sent: LastSent | null;
  /** The customer's name as it appears in their emails. */
  display_name?: string;
  display_name_set?: boolean;
}

export interface EmailSettings {
  global_enabled: boolean;
  default_on: boolean;
  send_weekday: number;
  send_time: string;
  timezone: string;
  updated_at: string | null;
  /** The weekly team batch: summary + previews of what customers would get. */
  internal_enabled: boolean;
  internal_recipients: string[];
  internal_last_sent: LastSent | null;
}

export type EmailKind = 'summary' | 'reminder' | 'internal';

export interface EmailSchedule {
  next_send: string;
  next_send_label: string;
  next_report_week: string;
  last_slot: string;
  send_label: string;
}

export interface Overview {
  status: 'ok' | 'warming';
  data: E2Snapshot | null;
  settings: EmailSettings;
  schedule: EmailSchedule;
  orgs: Record<string, OrgEmail>;
  sandbox: boolean;
  weekdays: string[];
  /** Customer names as the wall and the weekly emails show them. */
  labels?: Record<string, string>;
}

export interface Suggestion {
  email: string;
  name: string | null;
  source: string;
}

export interface EmailPreview {
  status: string;
  kind?: EmailKind;
  reminder_needed?: boolean;
  org: string | null;
  subject: string;
  html: string;
  week_start: string;
  week_end: string;
  partial: boolean;
  will_send: boolean;
  reason: string | null;
  recipients: string[];
}

export async function consoleApi<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE_URL}/api/e2/console${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((body && body.error) || `Request failed (${res.status})`);
  return body as T;
}

export type Status = 'on_pace' | 'behind' | 'not_recording' | 'never';

export const STATUS_LABEL: Record<Status, string> = {
  on_pace: 'On pace',
  behind: 'Behind',
  not_recording: 'Not recording',
  never: 'Never recorded',
};

/** Days since `lastRecording` relative to the snapshot's `today`. */
export function silentDays(lastRecording: string | null, today: string): number | null {
  if (!lastRecording) return null;
  const a = Date.parse(`${lastRecording.slice(0, 10)}T00:00:00Z`);
  const b = Date.parse(`${today}T00:00:00Z`);
  return Math.max(0, Math.round((b - a) / 86_400_000));
}

/**
 * Status against the console's "silent for N days" threshold: never recorded,
 * no recording in the last N days, else on pace / behind vs the daily target.
 */
export function statusOf(
  lastRecording: string | null,
  silent: number | null,
  pace: number,
  paceTarget: number,
  threshold: number,
): Status {
  if (!lastRecording) return 'never';
  if (silent != null && silent >= threshold) return 'not_recording';
  return pace >= paceTarget ? 'on_pace' : 'behind';
}

export function deviceSilent(d: E2Device, today: string): number | null {
  return d.silent_days !== undefined ? d.silent_days : silentDays(d.last_recording, today);
}

export const fmtH = (h: number | null | undefined, digits = 1) =>
  h == null ? '–' : h.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });

export function fmtStamp(s: string | null | undefined): string {
  if (!s) return '–';
  // Recording stamps are device-local wall time ("2026-10-28T08:00:00"), so
  // format the parts directly instead of letting Date shift the timezone.
  const [d, t] = s.split('T');
  const [y, m, day] = d.split('-').map(Number);
  const label = new Date(Date.UTC(y, m - 1, day)).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', timeZone: 'UTC',
  });
  return t ? `${label} ${t.slice(0, 5)}` : label;
}

export function fmtDay(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC',
  });
}

/** Dashboard days and clock times are Pacific, whatever the viewer's browser is set to. */
export const DASHBOARD_TZ = 'America/Los_Angeles';

export const pacificToday = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: DASHBOARD_TZ }).format(new Date());

export function fmtWhen(iso: string | null | undefined): string {
  if (!iso) return '–';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: DASHBOARD_TZ, timeZoneName: 'short',
  });
}

export function silentLabel(silent: number | null, lastRecording: string | null): string {
  if (!lastRecording) return 'Never';
  if (silent == null) return '–';
  if (silent === 0) return 'Today';
  return silent === 1 ? '1 day' : `${silent} days`;
}
